import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { RiSearchLine } from "react-icons/ri"
import { useNavigate } from "react-router-dom"

import {
  instantAddPlugin,
  warmupUpdateEventPluginConnection,
} from "@fuse/api/pluginApi"
import { usePluginSearch } from "@fuse/hooks/usePluginSearch"
import BottomSheet, {
  BottomSheetHandle,
} from "@fuse/sharedComponents/bottomSheet/BottomSheet"
import { useGlobalLoader } from "@fuse/sharedComponents/globalLoader/GlobalLoaderContext"
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { EFeatureFlags, EVENT_STATUS } from "@letshang/constants"
import { useRecoilState, useRecoilValue } from "recoil"
import { ROUTES } from "src/App"
import { useProcessFeatureFlags } from "src/settings/featureFlags"

import { eventPluginsAtom, getEventPlugins } from "@fuse/recoil/eventPlugins"
import { eventDetailsState } from "@fuse/recoil/events"
import staticPluginList from "@fuse/recoil/mockData/pluginList.json"
import { Plugin } from "@fuse/recoil/plugins"

import { createEventWeb, requestPluginAPI } from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/common"
import {
  getConnectionIconProps,
  getMainPluginIconProps,
  isPluginConnectedToEvent,
  renderIconByName,
} from "@fuse/utils/pluginConnectionHelpers"
import { getPluginStatusIcon, isInstantPlugin } from "@fuse/utils/pluginHelpers"

import IconPuzzle from "@fuse/assets/icons/plugin/icon-puzzle.svg"
import hangIcon from "@fuse/assets/images/hang-icon.png"
import ImgNoPlugin from "@fuse/assets/images/plugin/img-no-plugin.svg"

import EventPluginSkeleton from "./eventPluginSkeleton"

export interface EventPluginBottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

enum PluginCategory {
  ALL = "all",
  VISUAL = "visual",
  HOSTING_TOOLS = "hosting_tools",
  EXPERIENCES = "experiences",
}

type CategoryTab = PluginCategory

// Move this outside the main component
const RequestPluginBottomSheet = ({
  description,
  setDescription,
  setRequestPluginView,
  onSubmit,
}: {
  description: string
  setDescription: (v: string) => void
  setRequestPluginView: (v: boolean) => void
  onSubmit: () => void
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])
  return (
    <div className="flex flex-col h-full">
      <textarea
        ref={textareaRef}
        className="w-full bg-transparent text-white placeholder-white/60 border-none outline-none resize-none flex-1 p-2 text-base"
        placeholder="Describe the plugin you'd love to see."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        style={{
          minHeight: 120,
          background: "none",
          color: "#fff",
          fontSize: "16px",
          marginBottom: "auto",
        }}
      />
      <div className="flex-shrink-0">
        <div
          className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl cursor-pointer group transition-all duration-200"
          style={{
            background: !description
              ? "linear-gradient(0deg, rgba(194, 194, 194, 0.10) 0%, rgba(194, 194, 194, 0.10) 100%), rgba(127, 127, 127, 0.05)"
              : "linear-gradient(0deg, rgba(194, 194, 194, 0.30) 0%, rgba(194, 194, 194, 0.30) 100%), rgba(127, 127, 127, 0.25)",
            backgroundBlendMode: "overlay, luminosity",
            backdropFilter: "blur(10px)",
          }}
          onClick={() => {
            if (!description) return
            onSubmit()
          }}
        >
          <span
            className={`${!description ? "text-white35" : "text-white"} font-medium text-base transition-colors`}
          >
            Submit
          </span>
        </div>
      </div>
    </div>
  )
}

const EventPluginBottomSheet = forwardRef<
  EventPluginBottomSheetHandle,
  {
    eventId?: string
    onEventCreated?: (eventId: string) => void
  }
>(({ eventId, onEventCreated }, ref) => {
  const isCreateFlow = eventId === "new"
  const navigate = useNavigate()
  const bottomSheetRef = useRef<BottomSheetHandle>(null)

  // Check if feature flag is enabled
  const isCreateYourOwnEnabled = useProcessFeatureFlags(
    EFeatureFlags.ENABLE_PLUGIN_CREATION,
  )
  console.log(
    "[EventPluginBottomSheet] isCreateYourOwnEnabled",
    isCreateYourOwnEnabled,
  )

  const [eventPluginsState, setEventPluginsState] =
    useRecoilState(eventPluginsAtom)
  const [instantAddLoading, setInstantAddLoading] = useState<Set<string>>(
    new Set(),
  )
  const [description, setDescription] = useState("")
  const [isRequestPluginView, setRequestPluginView] = useState(false)
  const [eventDetails, setEventDetails] = useRecoilState(eventDetailsState)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [isCreatingDraft, setIsCreatingDraft] = useState(false)
  const [draftCreationPromise, setDraftCreationPromise] = useState<Promise<
    string | null
  > | null>(null)
  const [currentEventId, setCurrentEventId] = useState<string | undefined>(
    eventId,
  )

  // Update currentEventId when eventId prop changes
  useEffect(() => {
    setCurrentEventId(eventId)
  }, [eventId])

  // Use our new search hook
  const {
    searchState,
    updateSearchQuery,
    updateCategory,
    initializeSearch,
    resetSearch,
    forceApiSearch,
    hasResults,
    isLoading,
  } = usePluginSearch({ eventId: currentEventId })
  const { showLoader, hideLoader } = useGlobalLoader()

  // Get all plugins for this event to check connection status
  const plugins = currentEventId
    ? getEventPlugins(eventPluginsState, currentEventId)
    : []
  const enabledPlugins = eventDetails.eventDetails?.eventDocData
    ? (eventDetails.eventDetails.eventDocData as any)?.enabled_plugins || []
    : []

  // Auto-create draft event if needed
  const createDraftEventIfNeeded = async (): Promise<string | null> => {
    if (currentEventId && currentEventId !== "new") return currentEventId

    // If draft creation is already in progress, wait for it to complete
    if (draftCreationPromise) {
      return await draftCreationPromise
    }

    // If already creating, don't start another creation
    if (isCreatingDraft) {
      return null
    }

    setIsCreatingDraft(true)

    const creationPromise = (async () => {
      try {
        const result = await createEventWeb({
          eventStatus: EVENT_STATUS.DRAFT,
        })

        if (result.success && result.data?.eventId) {
          const newEventId = result.data.eventId
          onEventCreated?.(newEventId)
          setCurrentEventId(newEventId) // Update the local state
          return newEventId
        } else {
          showErrorToast("Failed to create draft event")
          return null
        }
      } catch (error) {
        console.error("Error creating draft event:", error)
        showErrorToast("Failed to create draft event")
        return null
      } finally {
        setIsCreatingDraft(false)
        setDraftCreationPromise(null)
      }
    })()

    setDraftCreationPromise(creationPromise)
    return await creationPromise
  }

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openModal: () => {
      bottomSheetRef.current?.openModal()
      forceApiSearch("", "all")
    },
    closeModal: () => {
      bottomSheetRef.current?.closeModal()
    },
  }))

  const [showStaticList, setShowStaticList] = useState(true)

  // Handle when bottom sheet opens/closes
  const handleBottomSheetOpen = () => {
    // Fire-and-forget warmup call
    warmupUpdateEventPluginConnection()
    if (searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
    if (!isDev()) {
      setShowStaticList(true) // Show static list immediately
    }
    // Initialize search when modal opens
    initializeSearch()
  }

  const handleBottomSheetClose = () => {
    // Reset search when modal closes
    if (isRequestPluginView) {
      setRequestPluginView(false)
    }
    resetSearch()
  }

  // Handle category change
  const handleCategoryChange = (category: string) => {
    updateCategory(category)
  }

  const handlePluginClick = async (pluginId: string, isConnected: boolean) => {
    setInstantAddLoading((prev) => new Set(prev).add(pluginId))
    if (isConnected) {
      setInstantAddLoading((prev) => {
        const newSet = new Set(prev)
        newSet.delete(pluginId)
        return newSet
      })
      showInfoToast("Plugin is already installed")
      return
    }
    console.log("[EventPluginBottomSheet] handlePluginClick", {
      pluginId,
      isCreateFlow,
      currentEventId,
    })

    // Auto-create draft if no event ID exists
    let eventIdToUse = currentEventId
    if (!eventIdToUse || eventIdToUse === "new") {
      showLoader()
      eventIdToUse = await createDraftEventIfNeeded().finally(() => {
        hideLoader()
      })
      if (!eventIdToUse) {
        setInstantAddLoading((prev) => {
          const newSet = new Set(prev)
          newSet.delete(pluginId)
          return newSet
        })
        showErrorToast("Failed to create draft event. Please try again.")
        return
      }
    }

    // Find the plugin object to check if it's instant
    const plugin = searchState.displayedPlugins.find((p) => p.id === pluginId)

    // Enhanced debugging for instant plugin detection
    console.log("[EventPluginBottomSheet] Plugin debug info:", {
      pluginId,
      plugin: plugin
        ? {
            id: plugin.id,
            name: plugin.manifest?.name,
            requiresZeroConfiguration:
              plugin.manifest?.requiresZeroConfiguration,
            approvalStatus: plugin.manifest?.approvalStatus,
          }
        : null,
      isInstantResult: plugin ? isInstantPlugin(plugin) : false,
    })

    // Check if this is an instant plugin
    if (plugin && isInstantPlugin(plugin)) {
      console.log(
        "[EventPluginBottomSheet] Plugin is instant, adding immediately",
      )

      // 1. START TRANSITION IMMEDIATELY - Add plugin to UI
      const newPlugin = {
        id: pluginId,
        version: plugin.manifest?.version || "1.0.0",
      }
      setEventDetails((prev) => {
        // Ensure we have the proper structure and handle null/undefined safely
        const currentEventDocData = prev.eventDetails?.eventDocData || {}
        const currentEnabledPlugins =
          (currentEventDocData as any)?.enabled_plugins || []

        return {
          ...prev,
          eventDetails: {
            ...prev.eventDetails,
            eventDocData: {
              ...((prev.eventDetails?.eventDocData as any) || {}),
              enabled_plugins: [...currentEnabledPlugins, newPlugin],
            } as any,
          },
        }
      })
      showInfoToast(`Working on adding ${plugin.manifest?.name} to your event!`)

      // 2. Close modal immediately for smooth UX
      bottomSheetRef.current?.closeModal()

      try {
        // 3. Call backend API
        const result = await instantAddPlugin({
          eventId: eventIdToUse,
          pluginId: pluginId,
        })

        if (!result.success) {
          throw new Error(result.error || "Failed to add plugin")
        }

        // 4. SUCCESS TOAST ONLY ON TRUE SUCCESS
        showSuccessToast(`${plugin.manifest?.name} added to your event!`)
      } catch (error) {
        // 5. ROLLBACK on error
        setEventDetails((prev) => {
          // Ensure we have the proper structure and handle null/undefined safely
          const currentEventDocData = prev.eventDetails?.eventDocData || {}
          const currentEnabledPlugins =
            (currentEventDocData as any)?.enabled_plugins || []

          return {
            ...prev,
            eventDetails: {
              ...prev.eventDetails,
              eventDocData: {
                ...((prev.eventDetails?.eventDocData as any) || {}),
                enabled_plugins: currentEnabledPlugins.filter(
                  (p: any) => p.id !== pluginId,
                ),
              } as any,
            },
          }
        })

        showErrorToast("Failed to add plugin")
        console.error("Error adding instant plugin:", error)
      } finally {
        setInstantAddLoading((prev) => {
          const newSet = new Set(prev)
          newSet.delete(pluginId)
          return newSet
        })
      }
    } else {
      // Regular plugin - navigate to configuration page
      console.log(
        "[EventPluginBottomSheet] Plugin is NOT instant, navigating to config page",
      )
      const pluginDetailsPath = ROUTES.PLUGIN_DETAILS.replace(
        ":pluginId",
        pluginId,
      )
      navigate(`${pluginDetailsPath}?event_id=${eventIdToUse}`)
    }
  }

  const handleCreateYourOwn = async () => {
    // Auto-create draft if no event ID exists
    const eventIdToUse = await createDraftEventIfNeeded()
    if (!eventIdToUse) {
      showErrorToast("Failed to create draft event. Please try again.")
      return
    }

    if (isCreateFlow) {
      showErrorToast(
        "Please create your event first before adding custom plugins.",
      )
      bottomSheetRef.current?.closeModal()
    } else {
      navigate(`${ROUTES.PLUGIN_STUDIO_CREATION}?event_id=${eventIdToUse}`)
    }
  }

  // New plugin icon for title
  const renderPluginTitleIcon = () => <img src={IconPuzzle} alt="IconPuzzle" />

  // Render an icon component from icon props
  const renderIcon = (
    iconProps: any,
    width: number = 20,
    height: number = 20,
  ) => {
    return (
      <svg
        width={width}
        height={height}
        viewBox={iconProps.viewBox}
        fill={iconProps.fill}
        xmlns="http://www.w3.org/2000/svg"
        className={iconProps.className}
      >
        {iconProps.fillPaths
          ? // Render fill paths (for custom SVGs like the plugin icon)
            iconProps.fillPaths.map((pathObj: any, index: number) => (
              <path
                key={index}
                d={pathObj.d}
                fill={pathObj.fill}
                fillOpacity={pathObj.fillOpacity}
                style={pathObj.style}
              />
            ))
          : // Render stroke paths (for existing icons)
            iconProps.paths.map((path: string, index: number) => (
              <path
                key={index}
                d={path}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
      </svg>
    )
  }

  // Render the users/events icon using the hang-icon asset
  const renderUsersIcon = () => (
    <img
      src={hangIcon}
      alt="Events"
      className="w-4 h-4 flex-shrink-0 opacity-60"
    />
  )

  // Mock data generator for plugin metadata
  const generatePluginMetadata = (pluginId: string) => {
    // Generate consistent mock data based on plugin ID
    const hash = pluginId.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0)
      return a & a
    }, 0)

    // const isFree = Math.abs(hash % 3) !== 0 // ~67% free
    const isFree = true
    const eventsUsed = Math.abs(hash % 1000) + 50 // 50-1049 events
    const likes = Math.abs(hash % 500) + 10 // 10-509 likes

    return {
      isFree,
      eventsUsed,
      likes,
    }
  }

  // Truncate description helper
  const truncateDescription = (description: string, maxLength: number = 80) => {
    if (description.length <= maxLength) return description
    return description.slice(0, maxLength).trim() + "..."
  }

  // Submit handler for plugin request
  const handleRequestPluginSubmit = async () => {
    showLoader()
    requestPluginAPI({ description: description })
      .then(() => {
        setRequestPluginView(false)
        setDescription("")
        showSuccessToast("Request submitted successfully")
      })
      .catch(() => {
        showErrorToast("Error requesting plugin request")
      })
      .finally(() => {
        hideLoader()
      })
  }
  useEffect(() => {
    if (!isLoading && !searchState.isSearching) {
      setShowStaticList(false)
    }
  }, [isLoading, searchState.isSearching])

  return (
    <BottomSheet
      ref={bottomSheetRef}
      title={isRequestPluginView ? "Request a plugin" : "Plugins"}
      titleIcon={!isRequestPluginView && renderPluginTitleIcon()}
      maxWidth="2xl"
      height="h-[85vh] max-h-[85vh]"
      zIndex={120}
      onOpen={handleBottomSheetOpen}
      onClose={handleBottomSheetClose}
      shouldCloseOnCloseButton={!isRequestPluginView}
    >
      {isRequestPluginView ? (
        <RequestPluginBottomSheet
          description={description}
          setDescription={setDescription}
          setRequestPluginView={setRequestPluginView}
          onSubmit={handleRequestPluginSubmit}
        />
      ) : (
        <>
          {/* Search */}
          <div className="relative mb-1">
            <RiSearchLine
              className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/60"
              size={20}
            />
            <input
              ref={searchInputRef}
              type="text"
              className="w-full h-[38px] px-2 pl-10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/30 transition-all"
              style={{
                background:
                  "linear-gradient(0deg, rgba(194, 194, 194, 0.50) 0%, rgba(194, 194, 194, 0.50) 100%), rgba(127, 127, 127, 0.20)",
                backgroundBlendMode: "overlay, luminosity",
                backdropFilter: "blur(10px)",
              }}
              placeholder="Search plugins..."
              value={searchState.searchQuery}
              onChange={(e) => updateSearchQuery(e.target.value)}
            />
            {searchState.isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/60"></div>
              </div>
            )}
          </div>

          {/* Categories */}
          {/* <div
          className="flex space-x-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {Object.values(PluginCategory).map((category) => {
            const isActive = searchState.activeCategory === category
            const labels = {
              [PluginCategory.ALL]: "All",
              [PluginCategory.VISUAL]: "Visual",
              [PluginCategory.HOSTING_TOOLS]: "Hosting tools",
              [PluginCategory.EXPERIENCES]: "Experiences",
            }
  
            return (
              <button
                key={category}
                className={`px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? "text-white ring-1 ring-white/20"
                    : "text-white/60 hover:text-white/80"
                }`}
                style={{
                  background: isActive
                    ? "linear-gradient(0deg, rgba(194, 194, 194, 0.50) 0%, rgba(194, 194, 194, 0.50) 100%), rgba(127, 127, 127, 0.40)"
                    : "transparent",
                  backgroundBlendMode: isActive
                    ? "overlay, luminosity"
                    : "normal",
                  backdropFilter: isActive ? "blur(12.3px)" : "none",
                }}
                onClick={() => handleCategoryChange(category)}
              >
                {labels[category as keyof typeof labels]}
              </button>
            )
          })}
        </div> */}

          {/* Results - Like textarea with flex-1 and natural overflow */}
          <div
            className="w-full flex-1 bg-transparent text-white overflow-y-auto overflow-x-hidden focus:outline-none [&::-webkit-scrollbar]:hidden"
            style={{
              scrollbarWidth: "none" /* Firefox */,
              msOverflowStyle: "none" /* Internet Explorer 10+ */,
            }}
          >
            {(isLoading || searchState.isSearching) && !showStaticList ? (
              <EventPluginSkeleton />
            ) : !hasResults && searchState.searchQuery ? (
              // No results state
              <div className="text-center py-12 flex flex-col items-center justify-center">
                <img src={ImgNoPlugin} alt="ImgNoPlugin" className="mb-4" />
                <h3 className="text-white text-lg font-medium mb-2">
                  No plugin found
                </h3>
                <p className="text-white/60 text-sm">
                  It seems like there are no plugin under that name.
                  <br /> You can request a plugin.
                </p>
                {/* <button
                  onClick={() => updateSearchQuery("")}
                  className="mt-4 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Clear search
                </button> */}
              </div>
            ) : (
              <div className="space-y-0">
                {searchState.displayedPlugins.map((plugin) => {
                  const isConnected = isPluginConnectedToEvent(
                    plugin.id,
                    enabledPlugins,
                  )
                  const isInstant = isInstantPlugin(plugin)
                  const mainIconProps = getMainPluginIconProps(isInstant)
                  const connectionIconProps = getConnectionIconProps(
                    isConnected,
                    isInstant,
                  )

                  return (
                    <div
                      key={plugin.id}
                      className={`flex items-start justify-between py-4 hover:bg-white/3 -mx-6 px-6 transition-all duration-200 cursor-pointer group ${instantAddLoading.has(plugin.id) ? "opacity-50 pointer-events-none" : ""}`}
                      onClick={() => {
                        if (instantAddLoading.has(plugin.id)) return // Prevent click
                        handlePluginClick(plugin.id, isConnected)
                      }}
                    >
                      <div className="flex items-start gap-3.5 flex-1 min-w-0">
                        <div className="flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity mt-0.5">
                          {/* {renderIcon(mainIconProps, 20, 20)} */}
                          {renderIconByName(plugin?.manifest?.iconName)}
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Title and badges */}
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-white text-base group-hover:text-white/90 transition-colors truncate text-left">
                              {plugin.manifest?.name}
                            </h3>
                          </div>

                          {/* Description */}
                          {plugin.manifest?.description && (
                            <p className="text-white/70 text-sm leading-relaxed mb-2 text-left">
                              {truncateDescription(plugin.manifest.description)}
                            </p>
                          )}

                          {/* Metadata row */}
                          <div className="flex items-center gap-4 text-xs text-white/50">
                            {(() => {
                              const metadata = generatePluginMetadata(plugin.id)
                              return (
                                <>
                                  {/* Free/Paid status */}
                                  <span className="font-medium">
                                    {metadata.isFree ? "Free" : "Paid"}
                                  </span>

                                  {/* Events used */}
                                  <div className="flex items-center gap-1.5">
                                    {renderUsersIcon()}
                                    <span>
                                      {metadata.eventsUsed.toLocaleString()}{" "}
                                      events
                                    </span>
                                  </div>

                                  {/* Likes */}
                                  <div className="flex items-center gap-1.5">
                                    <span>♡</span>
                                    <span>
                                      {metadata.likes.toLocaleString()}
                                    </span>
                                  </div>
                                </>
                              )
                            })()}
                          </div>

                          {/* Custom plugin badge */}
                          {(plugin as any).isCustom && (
                            <span className="text-xs bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded-full mt-2 inline-block font-medium">
                              Custom
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right side connection icon */}
                      <div className="flex-shrink-0 ml-4 opacity-60 group-hover:opacity-100 transition-opacity mt-0.5">
                        {instantAddLoading.has(plugin.id) ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white/80"></div>
                        ) : (
                          renderIcon(connectionIconProps, 18, 18)
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Create your own button - Always visible at bottom */}
          {isCreateYourOwnEnabled ? (
            <div className="flex-shrink-0">
              <div
                className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl cursor-pointer group transition-all duration-200"
                style={{
                  background:
                    "linear-gradient(0deg, rgba(194, 194, 194, 0.30) 0%, rgba(194, 194, 194, 0.30) 100%), rgba(127, 127, 127, 0.25)",
                  backgroundBlendMode: "overlay, luminosity",
                  backdropFilter: "blur(10px)",
                }}
                onClick={handleCreateYourOwn}
              >
                {renderIcon(getMainPluginIconProps(false), 20, 20)}
                <span className="text-white font-medium text-base group-hover:text-white/90 transition-colors">
                  Create your own
                </span>
              </div>
            </div>
          ) : (
            // Only show "Request a plugin" button when there are no search results
            !hasResults &&
            searchState.searchQuery && (
              <div className="flex-shrink-0">
                <div
                  className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl cursor-pointer group transition-all duration-200"
                  style={{
                    background:
                      "linear-gradient(0deg, rgba(194, 194, 194, 0.30) 0%, rgba(194, 194, 194, 0.30) 100%), rgba(127, 127, 127, 0.25)",
                    backgroundBlendMode: "overlay, luminosity",
                    backdropFilter: "blur(10px)",
                  }}
                  onClick={() => {
                    setRequestPluginView(true)
                  }}
                >
                  {/* {renderIcon(getMainPluginIconProps(false), 20, 20)} */}
                  <span className="text-white font-medium text-base group-hover:text-white/90 transition-colors">
                    Request a plugin
                  </span>
                </div>
              </div>
            )
          )}
        </>
      )}
    </BottomSheet>
  )
})

export default EventPluginBottomSheet
